import { useMemo, useState, type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DefinitionTooltip } from "src/components/common";
import { useLanguage } from "src/hooks/useLanguage";
import { formatNumber, wrapBidiIsolate } from "src/utils/numberFormat";
import type { SocialTypePerformance } from "../../../data/organicSocialMockData";
import {
  formatOrganicPostTypeLabel,
  getOrganicContentSectionDefinitionInfo,
} from "../organicMediaDefinitions";
import OrganicSelectMenu from "../OrganicSelectMenu";
import { SOCIAL_TYPE_METRIC_OPTIONS, type SocialTypeMetric } from "./socialReportConfig";
import {
  formatSocialMetricTick,
  getSocialPercentAxisConfig,
  getSocialBarChartMargin,
  SOCIAL_BAR_X_AXIS_HEIGHT,
  SocialBarTooltip,
  SocialContentTypeAxisTick,
  SocialDonutTooltip,
} from "./socialReportChartParts";

const CONTENT_TYPE_BAR_PAGE_SIZE = 4;

interface SocialReportBreakdownRowProps {
  typePerformance: SocialTypePerformance[];
  /** Third panel of the same row, matching the other content reports. */
  trendsPanel: ReactNode;
}

const SocialReportBreakdownRow: FC<SocialReportBreakdownRowProps> = ({
  typePerformance,
  trendsPanel,
}) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const [metric, setMetric] = useState<SocialTypeMetric>("engagementRate");
  const [barPage, setBarPage] = useState(1);

  const activeMetric =
    SOCIAL_TYPE_METRIC_OPTIONS.find(option => option.value === metric) ??
    SOCIAL_TYPE_METRIC_OPTIONS[0];

  const donutData = useMemo(
    () =>
      [...typePerformance]
        .sort((left, right) => right.contentCount - left.contentCount)
        .map(type => ({
          typeKey: type.key,
          name: formatOrganicPostTypeLabel(type.key, t),
          value: type.contentCount,
          color: type.color,
        })),
    [typePerformance, t]
  );

  const donutTotal = donutData.reduce((sum, slice) => sum + slice.value, 0);

  const barData = useMemo(
    () =>
      typePerformance.map(type => ({
        typeKey: type.key,
        type: type.key,
        value: type[metric] ?? 0,
        color: type.color,
      })),
    [typePerformance, metric]
  );

  const totalBarPages = Math.max(1, Math.ceil(barData.length / CONTENT_TYPE_BAR_PAGE_SIZE));
  const currentBarPage = Math.min(barPage, totalBarPages);
  const pagedBarData = barData.slice(
    (currentBarPage - 1) * CONTENT_TYPE_BAR_PAGE_SIZE,
    currentBarPage * CONTENT_TYPE_BAR_PAGE_SIZE
  );

  const axisConfig = useMemo(
    () =>
      activeMetric.format === "percent"
        ? getSocialPercentAxisConfig(barData.map(row => Number(row.value ?? 0)))
        : {},
    [barData, activeMetric.format]
  );

  const breakdownTitle = t("organicMedia.social.sections.contentTypeBreakdown");
  const performanceTitle = t("organicMedia.social.sections.contentPerformanceByType");

  return (
    <div className="organic-content-report__chart-grid">
      <div className="organic-content-panel organic-content-panel--content-breakdown">
        <div className="organic-content-panel__header">
          <div className="organic-content-panel__title">
            <h3>{breakdownTitle}</h3>
            <DefinitionTooltip
              label={breakdownTitle}
              info={getOrganicContentSectionDefinitionInfo("contentTypeBreakdown", t)}
              formulaTitle={t("organicMedia.common.formula")}
            />
          </div>
        </div>
        <div className="organic-content-panel__donut">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip
                content={<SocialDonutTooltip />}
                allowEscapeViewBox={{ x: false, y: true }}
              />
              <Pie
                data={donutData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="58%"
                outerRadius="86%"
                startAngle={90}
                endAngle={-270}
                paddingAngle={0}
                labelLine={false}
              >
                {donutData.map(slice => (
                  <Cell key={slice.typeKey} fill={slice.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="organic-content-panel__donut-center">
            <span className="organic-content-panel__donut-center-value">
              {formatNumber(donutTotal, {}, i18n.language)}
            </span>
            <span className="organic-content-panel__donut-center-label">
              {t("organicMedia.social.totalContent")}
            </span>
          </div>
        </div>
        <div
          className={`organic-content-panel__pie-legend ${
            donutData.length > 3
              ? "organic-content-panel__pie-legend--columns"
              : "organic-content-panel__pie-legend--centered"
          }`}
          aria-hidden
        >
          {donutData.map((slice, index) => (
            <div key={slice.typeKey} className="organic-content-panel__pie-legend-item">
              <span
                className={`organic-content-panel__pie-legend-swatch social-report__swatch--palette-${index}`}
              />
              <span className="organic-content-panel__pie-legend-text">{slice.name}</span>
              <strong>
                {formatNumber(slice.value, {}, i18n.language)}
                {donutTotal > 0
                  ? wrapBidiIsolate(
                      ` (${formatNumber((slice.value / donutTotal) * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%)`
                    )
                  : ""}
              </strong>
            </div>
          ))}
        </div>
      </div>

      <div className="organic-content-panel organic-content-panel--bar">
        <div className="organic-content-panel__header">
          <div className="organic-content-panel__title">
            <h3>{performanceTitle}</h3>
            <DefinitionTooltip
              label={performanceTitle}
              info={getOrganicContentSectionDefinitionInfo("contentPerformanceByType", t)}
              formulaTitle={t("organicMedia.common.formula")}
            />
          </div>
          <div className="organic-bars__select-wrap">
            <OrganicSelectMenu
              className="organic-bars__select"
              ariaLabel={t("organicMedia.contentReport.engagementMetricSelectAria")}
              value={metric}
              onChange={value => {
                setMetric(value as SocialTypeMetric);
                setBarPage(1);
              }}
              options={SOCIAL_TYPE_METRIC_OPTIONS.map(option => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
            />
          </div>
        </div>
        <div className="organic-content-panel__chart-fill">
          <ResponsiveContainer width="100%" height={340}>
            <BarChart
              data={pagedBarData}
              margin={getSocialBarChartMargin(isRTL)}
            >
              <CartesianGrid stroke="#e4e8ef" strokeDasharray="4 4" vertical={false} />
              <XAxis
                dataKey="type"
                reversed={isRTL}
                tick={<SocialContentTypeAxisTick />}
                axisLine={{ stroke: "#d7dde7" }}
                tickLine={false}
                tickMargin={8}
                interval={0}
                height={SOCIAL_BAR_X_AXIS_HEIGHT}
              />
              <YAxis
                orientation={isRTL ? "right" : "left"}
                width={70}
                tickMargin={isRTL ? 10 : 4}
                tick={(tickProps: { x?: number; y?: number; payload?: { value: number } }) => {
                  const { x = 0, y = 0, payload } = tickProps;
                  return (
                    <text
                      x={x}
                      y={y}
                      dy={4}
                      textAnchor={isRTL ? "start" : "end"}
                      direction="ltr"
                      fill="#5e6a7d"
                      fontSize={13}
                    >
                      {formatSocialMetricTick(
                        payload?.value ?? 0,
                        activeMetric.format,
                        i18n.language
                      )}
                    </text>
                  );
                }}
                axisLine={{ stroke: "#9aa5b8" }}
                tickLine={{ stroke: "#9aa5b8" }}
                domain={axisConfig.domain}
                ticks={axisConfig.ticks}
              />
              <Tooltip
                content={<SocialBarTooltip format={activeMetric.format} />}
                cursor={{ fill: "rgba(109, 40, 217, 0.08)" }}
                allowEscapeViewBox={{ x: true, y: true }}
              />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={36}>
                <LabelList
                  dataKey="value"
                  position="top"
                  dy={-8}
                  formatter={(value: number | string) =>
                    formatSocialMetricTick(value, activeMetric.format, i18n.language)
                  }
                  className="organic-bars__value-label"
                />
                {pagedBarData.map(entry => (
                  <Cell key={`${entry.typeKey}-${metric}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {barData.length > 0 ? (
          <div className="organic-bars__pagination">
            <button
              type="button"
              onClick={() => setBarPage(page => Math.max(1, page - 1))}
              disabled={currentBarPage === 1}
              aria-label={t("organicMedia.common.previousPage")}
            >
              {isRTL ? "›" : "‹"}
            </button>
            <span>
              {t("pagination.pageOf", {
                current: formatNumber(currentBarPage, {}, i18n.language),
                total: formatNumber(totalBarPages, {}, i18n.language),
              })}
            </span>
            <button
              type="button"
              onClick={() => setBarPage(page => Math.min(totalBarPages, page + 1))}
              disabled={currentBarPage === totalBarPages}
              aria-label={t("organicMedia.common.nextPage")}
            >
              {isRTL ? "‹" : "›"}
            </button>
          </div>
        ) : null}
      </div>

      {trendsPanel}
    </div>
  );
};

export default SocialReportBreakdownRow;
